import { useEffect, useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errMsg } from "@/lib/api";
import { Empty } from "@/components/kit";

export const Field = ({ f, value, onChange }) => {
  const id = `field-${f.name}`;
  return (
    <div className={f.wide ? "md:col-span-2" : ""}>
      <Label htmlFor={id} className="text-xs text-slate-400">{f.label}</Label>
      {f.type === "select" ? (
        <select id={id} data-testid={id} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
          className="mt-1 h-10 w-full rounded-md border border-[#1E3A6E] bg-[#0B1A33] px-3 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#D4AF37]">
          {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      ) : (
        <Input id={id} data-testid={id} type={f.type === "number" ? "text" : f.type || "text"} inputMode={f.type === "number" ? "decimal" : undefined} value={value ?? ""}
          onChange={(e) => onChange(e.target.value)} className="mt-1 bg-[#0B1A33]" required={f.required !== false} />
      )}
    </div>
  );
};

export const parseNum = (v) => {
  if (typeof v === "number") return v;
  let s = String(v).trim().replace(/R\$|\s/g, "");
  if (s.includes(",")) s = s.replace(/\./g, "").replace(",", ".");
  else if (/^\d{1,3}(\.\d{3})+$/.test(s)) s = s.replace(/\./g, "");
  return s === "" ? NaN : Number(s);
};

export function FormDialog({ open, onOpenChange, title, fields, initial, onSubmit, testid = "form-dialog" }) {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setForm(initial || Object.fromEntries(fields.map((f) => [f.name, f.default ?? ""])));
  }, [open, initial, fields]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const shown = (f) => !f.showIf || f.showIf(form);
    const num = (f) => (form[f.name] === "" || form[f.name] == null ? (f.nullable ? null : 0) : parseNum(form[f.name]));
    const bad = fields.find((f) => shown(f) && f.type === "number" && Number.isNaN(num(f)));
    if (bad) { toast.error(`Valor inválido em "${bad.label}". Use números, ex.: 1.500,00`); setBusy(false); return; }
    const payload = Object.fromEntries(fields.map((f) => [f.name, !shown(f) ? (f.nullable ? null : f.default ?? (f.type === "number" ? 0 : "")) : f.type === "number" ? num(f) : form[f.name] === "" && f.nullable ? null : form[f.name]]));
    try {
      await onSubmit(payload);
      onOpenChange(false);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-[#1E3A6E] bg-[#0B1A33] sm:max-w-xl" data-testid={testid}>
        <DialogHeader><DialogTitle className="font-display">{title}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {fields.filter((f) => !f.showIf || f.showIf(form)).map((f) => <Field key={f.name} f={f} value={form[f.name]} onChange={(v) => setForm((s) => ({ ...s, [f.name]: v }))} />)}
          <div className="flex justify-end gap-2 md:col-span-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} data-testid={`${testid}-cancel`}>Cancelar</Button>
            <Button type="submit" disabled={busy} data-testid={`${testid}-submit`}>{busy ? "Salvando..." : "Salvar"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function ResourceTable({ wid, res, fields, columns, title, addLabel = "Adicionar", rows, onChanged, readOnly, mapEdit }) {
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);

  const save = async (payload) => {
    if (edit) await api.put(`/workspaces/${wid}/${res}/${edit.id}`, payload);
    else await api.post(`/workspaces/${wid}/${res}`, payload);
    toast.success(edit ? "Registro atualizado" : "Registro adicionado");
    onChanged();
  };
  const remove = async (row) => {
    if (!window.confirm("Excluir este registro?")) return;
    try {
      await api.delete(`/workspaces/${wid}/${res}/${row.id}`);
      toast.success("Registro excluído");
      onChanged();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const initial = edit ? Object.fromEntries(fields.map((f) => [f.name, (mapEdit ? mapEdit(edit) : edit)[f.name] ?? ""])) : null;

  return (
    <div data-testid={`${res}-table-wrap`}>
      <div className="mb-4 flex items-center justify-between gap-3">
        <h3 className="text-lg font-semibold">{title}</h3>
        {!readOnly && (
          <Button size="sm" onClick={() => { setEdit(null); setOpen(true); }} data-testid={`${res}-add-btn`}>
            <Plus className="mr-1 h-4 w-4" /> {addLabel}
          </Button>
        )}
      </div>
      {rows.length === 0 ? <Empty text="Nenhum registro ainda." /> : (
        <div className="overflow-x-auto rounded-lg border border-[#1E3A6E]">
          <table className="w-full min-w-[640px] text-sm" data-testid={`${res}-table`}>
            <thead className="bg-[#0B1A33] text-left text-xs uppercase tracking-wider text-slate-400">
              <tr>{columns.map((c) => <th key={c.key} className={`px-3 py-3 ${c.right ? "text-right" : ""}`}>{c.label}</th>)}<th className="w-20" /></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-[#1E3A6E]/60 transition-colors hover:bg-[#142C59]/60" data-testid={`${res}-row`}>
                  {columns.map((c) => <td key={c.key} className={`px-3 py-3 ${c.right ? "num text-right" : ""}`}>{c.render ? c.render(r) : r[c.key]}</td>)}
                  <td className="px-2 text-right">
                    {r.readonly ? (
                      <span className="whitespace-nowrap rounded-full border border-[#D4AF37]/40 px-2 py-0.5 text-[10px] text-[#D4AF37]" title={`Lançado no Diagnóstico Empresarial (${r.company_name}). Edite por lá.`} data-testid={`${res}-from-biz-${r.id}`}>Diagnóstico</span>
                    ) : !readOnly && (
                      <div className="flex justify-end gap-1">
                        <button className="rounded p-1.5 text-slate-400 hover:bg-[#1E3A6E] hover:text-white" onClick={() => { setEdit(r); setOpen(true); }} data-testid={`${res}-edit-${r.id}`} aria-label="Editar"><Pencil className="h-4 w-4" /></button>
                        <button className="rounded p-1.5 text-slate-400 hover:bg-red-500/20 hover:text-red-400" onClick={() => remove(r)} data-testid={`${res}-delete-${r.id}`} aria-label="Excluir"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <FormDialog open={open} onOpenChange={setOpen} title={edit ? "Editar" : addLabel} fields={edit ? fields.filter((f) => !f.createOnly) : fields}
        initial={initial} onSubmit={save} testid={`${res}-dialog`} />
    </div>
  );
}
